import { useLocalization } from "@fluent/react";
import * as Sentry from "@sentry/react";
import { LoadingEllipses } from "cerulean";
import { useEffect, useRef } from "react";
import { NavigationType, useBlocker } from "react-router";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetConsumerApplicationQuery } from "@lending/hooks/queries/useGetConsumerApplicationQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import { useStore } from "../../../../contexts/StoreContext";
import styles from "../Processing/Processing.module.css";

const POLL_INTERVAL_MS = 2000;
const SUBMITTING_TIMEOUT_MS = 120000;

function MembershipShareSubmittingDocuments() {
  const navigate = useNavigateWithSearch();
  const { l10n } = useLocalization();
  const { data } = useStore();
  const { refetch } = useGetConsumerApplicationQuery(data.accountApplicationUuid);
  const isPolling = useRef(false);
  const isRequestInFlight = useRef(false);
  const refetchRef = useRef(refetch);

  useBlocker((navigation) => {
    return isPolling.current && navigation.historyAction === NavigationType.Pop;
  });

  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!data.accountApplicationUuid) {
      void navigate("../status", { replace: true });
      return;
    }

    isPolling.current = true;
    const timeoutAt = Date.now() + SUBMITTING_TIMEOUT_MS;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stopPolling = () => {
      isPolling.current = false;
      isRequestInFlight.current = false;
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const pollApplication = async () => {
      if (!isPolling.current || isRequestInFlight.current) {
        return;
      }

      isRequestInFlight.current = true;

      try {
        const { data: application } = await refetchRef.current();

        if (!isPolling.current) {
          return;
        }

        if (application?.state !== "docs_requested" || Date.now() >= timeoutAt) {
          stopPolling();
          void navigate("../status", { replace: true });
        }
      } catch (error) {
        Sentry.captureException(error);
        stopPolling();
        void navigate("../status", { replace: true });
      } finally {
        isRequestInFlight.current = false;
      }
    };

    intervalId = setInterval(() => {
      void pollApplication();
    }, POLL_INTERVAL_MS);

    void pollApplication();

    return () => {
      stopPolling();
    };
  }, [data.accountApplicationUuid, navigate]);

  return (
    <PageLayout>
      <div className="alignChild--center--center margin--top--xxl">
        <div>
          <div
            className="alignChild--center--center margin--bottom--xl"
            style={{ position: "relative" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="150"
              height="150"
              viewBox="0 0 150 150"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M70.6645 0.0195305C106.349 -0.78658 138.977 23.4409 148.334 57.8864C156.608 88.3471 132.703 114.826 106.721 132.75C83.1586 149.004 52.9328 156.214 29.3807 139.945C4.19928 122.55 -5.56762 90.5215 3.09307 61.1673C12.5968 28.9559 37.0889 0.777998 70.6645 0.0195305Z"
                fill="var(--theme-primary)"
                fillOpacity="0.2"
              />
            </svg>
            <div className={styles.loadingEllipses}>
              <LoadingEllipses />
            </div>
          </div>
          <div className="alignChild--center--center" style={{ flexDirection: "column" }}>
            <div className="fontWeight--semibold fontSize--l margin--top--xxl">
              {l10n.getString(
                "membership-share-submitting-documents-title",
                null,
                "Submitting your application..."
              )}
            </div>
            <div
              className="textAlign--center margin--top--xxl"
              style={{ color: "var(--font-color-secondary)" }}
            >
              {l10n.getString(
                "membership-share-submitting-documents-message-1",
                null,
                "This can take up to a minute —"
              )}
            </div>
            <div className="textAlign--center" style={{ color: "var(--font-color-secondary)" }}>
              {l10n.getString(
                "membership-share-submitting-documents-message-2",
                null,
                "please do not close this window."
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { MembershipShareSubmittingDocuments };
